import * as React from 'react';

/** Material UI Packages. */
import { Typography, Box, TextField, Autocomplete } from '@mui/material';
import Grid from '@mui/material/Grid2';

import parse from 'autosuggest-highlight/parse';

import {
    isSamePlaceOption,
    placeOptionLabel,
    placeOptionParts,
    useGooglePlacesAutocomplete,
} from 'shared/lib/handleGoogle';

export default function GoogleLocation({
    keyname,
    label,
    value,
    handleRequiredState,
    disabled,
    sx,
    typographyStyle,
    boxStyle,
}) {
    const { options, handleInputChange, endSession, ready } = useGooglePlacesAutocomplete({ value });

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '25px', ...boxStyle }}>
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...typographyStyle,
                }}
            >
                {label}
            </Typography>
            <Autocomplete
                size="small"
                sx={{
                    width: '180px',
                    '& .MuiInputBase-input': {
                        fontSize: '13px',
                        height: '12px',
                    },
                    ...sx,
                }}
                getOptionLabel={placeOptionLabel}
                isOptionEqualToValue={isSamePlaceOption}
                filterOptions={(x) => x}
                options={options}
                autoComplete
                includeInputInList
                filterSelectedOptions
                disableClearable
                disabled={disabled || !ready}
                value={value}
                noOptionsText="No locations"
                onChange={(event, newValue) => {
                    endSession();
                    // The committed value rides along as an option; re-picking it has nothing to look up.
                    if (!newValue?.place_id) return;
                    handleRequiredState(newValue, keyname);
                }}
                onInputChange={handleInputChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
                renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    const { mainText, matches, secondaryText } = placeOptionParts(option);
                    const parts = parse(
                        mainText,
                        matches.map((match) => [match.offset, match.offset + match.length])
                    );
                    return (
                        <li key={key} {...optionProps}>
                            <Grid container sx={{ alignItems: 'center' }}>
                                <Grid>
                                    {parts.map((part, index) => (
                                        <Box
                                            key={index}
                                            component="span"
                                            sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                                        >
                                            {part.text}
                                        </Box>
                                    ))}
                                    {secondaryText ? (
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {secondaryText}
                                        </Typography>
                                    ) : null}
                                </Grid>
                            </Grid>
                        </li>
                    );
                }}
            />
        </Box>
    );
}
